import React, { useRef, useEffect, useState } from 'react'
import * as d3 from 'd3'
import { rounded_rect } from '../../utils/graphUtils'
import { roundOffAndFormatNumberWithCurrency } from '../../utils/utils'

interface ComparisonBarGraphProps {
  regularValue: number
  directValue: number
}

// Remove props from component signature
const ComparisonBarGraph: React.FC<ComparisonBarGraphProps> = ({ regularValue, directValue }) => {
  const svgRef = useRef<SVGSVGElement | null>(null)
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({ width: 0, height: 0 })

  useEffect(() => {
    const resizeListener = () => {
      if (svgRef.current) {
        const { width, height } = svgRef.current.getBoundingClientRect()
        setDimensions({ width, height })
      }
    }
    window.addEventListener('resize', resizeListener)
    resizeListener()
    return () => window.removeEventListener('resize', resizeListener)
  }, [])

  useEffect(() => {
    if (!svgRef.current || dimensions.width === 0 || dimensions.height === 0) return

    // Reserve space for top and bottom text
    let topTextHeight = 0
    let bottomTextHeight = 0

    // Calculate scale domain to handle negative values
    let domainMin = Math.min(0, regularValue, directValue)
    let domainMax = Math.max(0, regularValue, directValue)

    const maxValue = Math.max(regularValue, directValue)
    const minValue = Math.min(regularValue, directValue)

    let origin = 0

    // Add padding to domain
    if (minValue > 0 && maxValue > 0) {
      domainMin = minValue * 0.8
      domainMax = maxValue
      origin = domainMin
      topTextHeight = 25
      bottomTextHeight = 55
    } else if (minValue < 0 && maxValue < 0) {
      domainMin = minValue
      domainMax = maxValue * 0.8
      origin = domainMax
      topTextHeight = 0
      bottomTextHeight = 85
    } else {
      domainMin = minValue * 1.2
      domainMax = maxValue
      origin = 0
      topTextHeight = 25
      bottomTextHeight = 80
    }

    const margin = { top: topTextHeight, right: 20, bottom: bottomTextHeight, left: 20 }
    const width = dimensions.width - margin.left - margin.right
    const height = dimensions.height - margin.top - margin.bottom

    const svg = d3
      .select(svgRef.current)
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', `0 0 ${dimensions.width} ${dimensions.height}`)

    svg.selectAll('*').remove()

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`)

    // Bar dimensions
    const barWidth = 60
    const gapBetweenBars = 60
    const totalWidth = 2 * barWidth + gapBetweenBars
    const startX = Math.max(0, (width - totalWidth) / 2)

    // Create scale
    const y = d3.scaleLinear().domain([domainMin, domainMax]).range([height, 0])
    const y0 = y(origin)

    // Draw bars
    const drawBar = (value: number, x: number, color: string) => {
      let barHeight: number
      let yPos: number
      let roundTop: boolean
      let roundBottom: boolean
      let labelY: number

      if (value >= 0) {
        yPos = y(value)
        barHeight = y0 - yPos
        roundTop = true
        roundBottom = false
        labelY = yPos - 10
      } else {
        // value < 0
        yPos = y0
        barHeight = y(value) - y0
        roundTop = false
        roundBottom = true
        labelY = y(value) + 20
      }

      // Ensure a minimum bar height for visibility if value is not 0
      if (value !== 0 && barHeight < 2) {
        barHeight = 2
        if (value > 0) {
          yPos = y0 - barHeight
        }
      }

      // Draw bar
      g.append('path')
        .attr('d', rounded_rect(x, yPos, barWidth, barHeight, 4, roundTop, roundTop, roundBottom, roundBottom))
        .attr('fill', color)

      // Add value label
      g.append('text')
        .attr('x', x + barWidth / 2)
        .attr('y', labelY)
        .attr('text-anchor', 'middle')
        .attr('font-size', '12px')
        .attr('font-weight', '500')
        .attr('fill', color)
        .text(`${value < 0 ? '-' : ''} ${roundOffAndFormatNumberWithCurrency(value)}`)
    }

    // Draw the bars using the hardcoded values
    drawBar(regularValue, startX, '#095EB2')
    drawBar(directValue, startX + barWidth + gapBetweenBars, '#CC5C2D')

    // Add x-axis line
    g.append('line')
      .attr('x1', 0)
      .attr('y1', y0)
      .attr('x2', width)
      .attr('y2', y0)
      .attr('stroke', '#E77464')
      .attr('stroke-width', 0.5)
      .attr('opacity', 0.3)

    // Add labels for the bars, positioned based on the value
    const addCategoryLabel = (value: number, x: number, color: string, textLines: string[]) => {
      const yPos = regularValue < 0 || directValue < 0 ? y(domainMin) + 45 : y0 + 25
      const text = g
        .append('text')
        .attr('x', x)
        .attr('y', yPos)
        .attr('text-anchor', 'middle')
        .attr('font-size', '12px')
        .attr('font-weight', '400')
        .attr('fill', color)

      textLines.forEach((line, i) => {
        text
          .append('tspan')
          .attr('x', x)
          .attr('dy', i === 0 ? '0' : '1.2em')
          .text(line)
      })
    }

    addCategoryLabel(regularValue, startX + barWidth / 2, '#095EB2', ['Regular Fund', 'Returns (Current)'])
    addCategoryLabel(directValue, startX + barWidth + gapBetweenBars + barWidth / 2, '#CC5C2D', [
      'Direct Fund',
      'Returns'
    ])
  }, [dimensions, regularValue, directValue])

  return <svg ref={svgRef} style={{ width: '100%', height: '100%' }}></svg>
}

export default ComparisonBarGraph
